import { Component, Input, OnDestroy } from '@angular/core';
import { TranslatorService } from '../../../services/translator_service';
import { ResponsiveService } from '../../../services/responsive.service';
import { ApiQuery } from '../../../web-services/api/api.query';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { RTLDivDirectiveDirective } from '../../../directives/rtldiv-directive.directive';
import { GoogleMapsModule } from '@angular/google-maps';
import moment from 'moment';
import { UtilService } from '../../../services/util.service';
import { ApiService } from '../../../web-services/api/api.service';
import { ApiStore } from '../../../web-services/api/api.store';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-bin-location',
  imports: [CommonModule,FormsModule,TranslatePipe,RTLDivDirectiveDirective,GoogleMapsModule,NgbTooltipModule],
  templateUrl: './bin-location.html',
  styleUrl: './bin-location.scss',
})
export class BinLocation {
	isMobile: boolean;
	translationsObj;
	currentLang;
	usageType = 1;
	marker;
	map;
	private readonly destroy$ = new Subject<void>();
	selectedBinInfo: any = null;
	gpsRequested: boolean = false;
	displayGpsApproval: boolean = false;
	showLocationHistory: boolean = false;
	gpsLocationHistory: any[] = [];
	locationHistoryMarkers: google.maps.Marker[] = [];
	locationHistoryPolylines: google.maps.Polyline[] = [];
	displaySuccessAction: boolean = false;
	displayRejectAction: boolean = false;
	displayTheSameLocationAction: boolean = false;
	displayGpsFailedAction: boolean = false;
	displayGpsLowAccuracyAction: boolean = false;
	expectedApprovalItem: any = null;
	expectedApprovalAddress: string = '';
	user_role_id = 0;
	mapOptions: google.maps.MapOptions = {
		clickableIcons: false,
		streetViewControl : false,
		fullscreenControl : true,
		mapTypeControl : true
	};

	constructor(private translator: TranslatorService,private responsiveService: ResponsiveService,private apiQuery:ApiQuery,
    private utilService: UtilService,private apiService:ApiService,private apiStore:ApiStore) {
			this.translator.currentLangEmitter$
			.pipe(takeUntil(this.destroy$))
			.subscribe(async value=>{						
			  this.translationsObj = await this.translator.getTranslation(value).toPromise()			 
			  this.currentLang = value;
		})
	}

  ngOnInit() {
	this.onResize();
	this.responsiveService.checkWidth();
    this.apiQuery.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
		if (!user) {
			return;
		}						
		if(user["usage_type_id"] != 1){
			this.usageType = user["usage_type_id"];
		}	
		this.user_role_id = user["user_role_id"];
	});
  }

  onResize() {
		this.responsiveService.getMobileStatus().pipe(takeUntil(this.destroy$)).subscribe(isMobile => {
		  this.isMobile = isMobile;
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

  loadAPIWrapper(map: any) {
	setTimeout(() => {
		this.map = map;
		if(!this.isMobile){
			this.addYourLocationButton(this.map);
		}
		this.apiQuery.binInfo$.pipe(takeUntil(this.destroy$)).subscribe((info) => {
			this.selectedBinInfo = info;			
			// Always clear previous location history markers when switching bins
			this.clearLocationHistoryMarkers();
			this.clearLocationHistoryPolylines();
			this.showLocationHistory = false;
			this.gpsLocationHistory = [];
			this.displayGpsApproval = false;
			this.expectedApprovalItem = null;
			this.expectedApprovalAddress = '';
			this.displayGpsFailedAction = false;
			this.displayGpsLowAccuracyAction = false;
			
			if(info){
				const dataToSend = {
					"bin_id" : this.selectedBinInfo.bin_id
				};
				this.apiService.getBinGpsLocationHistory(dataToSend).pipe(takeUntil(this.destroy$)).subscribe((response:any) => {
					if(response.GpsHistoryObj == null){
						this.gpsRequested = false;
					}else if(response.GpsHistoryObj?.length > 0){
						this.gpsLocationHistory = response.GpsHistoryObj;
						//get the last item in the array
						const lastItem = this.gpsLocationHistory[this.gpsLocationHistory.length - 1];
						if(lastItem.request_status == 1 || lastItem.request_status == 2){
							this.gpsRequested = true;
						}else if(this.isPendingApprovalStatus(lastItem.request_status)){
							if(!this.hasCoordinates(lastItem)){
								this.gpsRequested = false;
								this.displayGpsFailedAction = true;
							}else{
							const sameCoords =
								lastItem.bin_lat == this.selectedBinInfo['Bin_location'].bin_latitude &&
								lastItem.bin_lon == this.selectedBinInfo['Bin_location'].bin_longitude;
								if(sameCoords){
									this.gpsRequested = true;
									this.displayTheSameLocationAction = true;
								}else{
									this.gpsRequested = true;
								}
								this.displayGpsLowAccuracyAction = lastItem.request_status == 7;
							}
						}else if(lastItem.request_status == 4 || lastItem.request_status == 5){
							this.gpsRequested = false;
						}else if(lastItem.request_status == 6){
							this.gpsRequested = false;
							this.displayGpsFailedAction = true;
						}
						this.displayGpsApproval = this.shouldDisplayGpsApproval(this.gpsLocationHistory);
						this.expectedApprovalItem = this.lastPendingApprovalItem(this.gpsLocationHistory);
						
						if (this.expectedApprovalItem && this.expectedApprovalItem.bin_lat && this.expectedApprovalItem.bin_lon) {
							this.getAddressFromCoordinates(
								Number(this.expectedApprovalItem.bin_lat),
								Number(this.expectedApprovalItem.bin_lon)
							);
						}
						if (this.displayGpsApproval) {
							this.showLocationHistory = true;
							this.onShowLocationHistoryChange();
						}
					}
				});				
				this.loadAPIWrapperInternal(this.map);
			}
		});
	}, 0);
  }

  loadAPIWrapperInternal(map: any) {
		if (!map || !this.selectedBinInfo) { return; }
		
		const binLat = Number(this.selectedBinInfo['Bin_location']?.bin_latitude);
		const binLng = Number(this.selectedBinInfo['Bin_location']?.bin_longitude);
		
		map.setCenter({ lat: binLat, lng: binLng });		
		this.addMarker(map);
	}

	get lastGpsStatus4Item(): any {
		if (!this.gpsLocationHistory || this.gpsLocationHistory.length === 0) {
			return null;
		}
		const status4Items = this.gpsLocationHistory.filter(item => item.request_status == 4);
		return status4Items.length > 0 ? status4Items[status4Items.length - 1] : null;
	}

	private isPendingApprovalStatus(status: any): boolean {
		return status == 3 || status == 7;
	}

	private hasCoordinates(item: any): boolean {
		return item != null && item.bin_lat != null && item.bin_lat !== '' && item.bin_lon != null && item.bin_lon !== '';
	}

	private lastPendingApprovalItem(responseItems: any[]): any {
		if (!responseItems || responseItems.length === 0) {
			return null;
		}
		for (let index = responseItems.length - 1; index >= 0; index--) {
			if (this.isPendingApprovalStatus(responseItems[index].request_status)) {
				return responseItems[index];
			}
		}
		return null;
	}

	shouldDisplayGpsApproval(responseItems) {
		const lastStatus3Item = this.lastPendingApprovalItem(responseItems);

		if (!lastStatus3Item) {
		  return false;
		}

		return this.hasCoordinates(lastStatus3Item);
	}

	approveGpsResponse(){
		const lastStatus3Item = this.lastPendingApprovalItem(this.gpsLocationHistory);
		if(!lastStatus3Item){
			return;
		}

		const dataToSend = {
			"request_status": 4,	
			"request_index": lastStatus3Item.request_index 	
		};
		this.apiService.setBinsGpsCoordinates(dataToSend).pipe(takeUntil(this.destroy$)).subscribe((v:any) => {
			this.displaySuccessAction = true;
			this.displayTheSameLocationAction = false;
			const dataToSend = {
				"bin_id_list" : [[Number(this.selectedBinInfo.bin_id),Number(this.selectedBinInfo.cluster_id)]],
				"address": this.expectedApprovalAddress
			};
			this.apiService.setBinThresholdsAndRestrictions(dataToSend).pipe(takeUntil(this.destroy$)).subscribe((v:any) => {
				this.apiService.getBinInfo(this.selectedBinInfo.bin_id);
				this.apiService.getfilteredBinsInfo(JSON.parse(sessionStorage.getItem("chosenSites")));
				this.apiStore.update({ allFilteredBasicBins: [] });                    
				this.apiStore.update({ filteredBasicBins: [] }); 
				this.apiService.getfilteredBasicBinsInfo(JSON.parse(sessionStorage.getItem("chosenSites")));
			});
		});
	}

	rejectGpsResponse(){
		const lastStatus3Item = this.lastPendingApprovalItem(this.gpsLocationHistory);
		if(!lastStatus3Item){
			return;
		}

		const dataToSend = {
			"request_status": 5,
			"request_index": lastStatus3Item.request_index 	
		};
		this.apiService.setBinsGpsCoordinates(dataToSend).pipe(takeUntil(this.destroy$)).subscribe((v:any) => {
			this.displayRejectAction = true;
			this.displayTheSameLocationAction = false;
			this.apiService.getBinInfo(this.selectedBinInfo.bin_id);
			this.apiService.getfilteredBinsInfo(JSON.parse(sessionStorage.getItem("chosenSites")));
			this.apiStore.update({ allFilteredBasicBins: [] });                    
			this.apiStore.update({ filteredBasicBins: [] }); 
			this.apiService.getfilteredBasicBinsInfo(JSON.parse(sessionStorage.getItem("chosenSites")));
		});
	}
  
	addMarker = (map: any) => {
		this.clearMarkers();
		const icon = this.BinToIcon(this.selectedBinInfo['Bin_live_Data'].color_status_id);
		this.marker = new google.maps.Marker({
			position: {
				lat: this.selectedBinInfo['Bin_location'].bin_latitude,
				lng: this.selectedBinInfo['Bin_location'].bin_longitude
			},
			map: map,
			icon: icon
		});
	}

  clearMarkers = () => {
		if (!this.marker) { return; }
		this.marker.setMap(null);
	}

	clearLocationHistoryMarkers = () => {
		this.locationHistoryMarkers.forEach(marker => {
			marker.setMap(null);
		});
		this.locationHistoryMarkers = [];
	}

	clearLocationHistoryPolylines = () => {
		this.locationHistoryPolylines.forEach(polyline => {
			polyline.setMap(null);
		});
		this.locationHistoryPolylines = [];
	}

	onShowLocationHistoryChange = () => {
		if (this.showLocationHistory) {
			this.displayLocationHistory();
		} else {
			this.clearLocationHistoryMarkers();
			this.clearLocationHistoryPolylines();
		}
	}

	displayLocationHistory = () => {
		if (!this.map || !this.selectedBinInfo || !this.gpsLocationHistory || this.gpsLocationHistory.length === 0) {
			return;
		}

		this.clearLocationHistoryMarkers();
		this.clearLocationHistoryPolylines();

		const status4Items = this.gpsLocationHistory.filter(item => item.request_status == 4);
		const status3Items = this.gpsLocationHistory.filter(item => this.isPendingApprovalStatus(item.request_status));

		const lastStatus3Item = status3Items[status3Items.length - 1];
		const greyMarkersItems = status4Items.slice(0, -1).filter(item => Number(item.bin_lat) != 0 && Number(item.bin_lon) != 0);
		greyMarkersItems.forEach(item => {
			if (item.bin_lat && item.bin_lon) {
				const marker = new google.maps.Marker({
					position: {
						lat: Number(item.bin_lat),
						lng: Number(item.bin_lon)
					},
					map: this.map,
					icon: '../../assets/images/bins/all/past_status.svg'
				});
				this.locationHistoryMarkers.push(marker);
			}
		});
		// Add green marker for selectedBinInfo location
		const selectedBinLat = Number(this.selectedBinInfo['Bin_location']?.bin_latitude);
		const selectedBinLng = Number(this.selectedBinInfo['Bin_location']?.bin_longitude);
		
		if (selectedBinLat!=null && selectedBinLng!=null) {
			const selectedBinMarker = new google.maps.Marker({
				position: {
					lat: selectedBinLat,
					lng: selectedBinLng
				},
				map: this.map,
				icon: '../../assets/images/bins/all/current_status.svg'
			});
			this.locationHistoryMarkers.push(selectedBinMarker);
		}

		// Add green marker with question mark for the last object from getBinGpsLocationHistory
		if (lastStatus3Item && lastStatus3Item.bin_lat && lastStatus3Item.bin_lon) {
			const lastItemMarker = new google.maps.Marker({
				position: {
					lat: Number(lastStatus3Item.bin_lat),
					lng: Number(lastStatus3Item.bin_lon)
				},
				map: this.map,
				icon: '../../assets/images/bins/all/approved_status.svg'
			});
			this.locationHistoryMarkers.push(lastItemMarker);

			// Add green arrow from selectedBinInfo marker to question mark marker
			if (selectedBinLat!=null && selectedBinLng!=null) {
				const greenArrow = new google.maps.Polyline({
					path: [
						{ lat: selectedBinLat, lng: selectedBinLng },
						{ lat: Number(lastStatus3Item.bin_lat), lng: Number(lastStatus3Item.bin_lon) }
					],
					geodesic: true,
					strokeColor: '#006EEF',
					strokeOpacity: 0,
					strokeWeight: 1,
					map: this.map,
					icons: [{
						icon: {
							path: 'M -2,-2 L 2,-2 L 2,2 L -2,2 Z',
							strokeColor: '#006EEF',
							fillColor: '#006EEF',
							fillOpacity: 1,
							strokeOpacity: 1,
							strokeWeight: 1
						},
						offset: '0%',
						repeat: '12px'
					}, {
						icon: {
							path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
							scale: 3.5,
							strokeColor: '#006EEF',
							strokeWeight: 1,
							fillColor: '#006EEF',
							fillOpacity: 1
						},
						offset: '100%'
					}]
				});
				this.locationHistoryPolylines.push(greenArrow);
			}
		}

		// Connect grey markers in sequence
		for (let i = 0; i < greyMarkersItems.length - 1; i++) {
			const currentItem = greyMarkersItems[i];
			const nextItem = greyMarkersItems[i + 1];
			
			if (currentItem.bin_lat && currentItem.bin_lon && nextItem.bin_lat && nextItem.bin_lon) {
				const blackArrow = new google.maps.Polyline({
					path: [
						{ lat: Number(currentItem.bin_lat), lng: Number(currentItem.bin_lon) },
						{ lat: Number(nextItem.bin_lat), lng: Number(nextItem.bin_lon) }
					],
					geodesic: true,
					strokeColor: '#68747A',
					strokeOpacity: 0,
					strokeWeight: 1,
					map: this.map,
					icons: [{
						icon: {
							path: 'M -2,-2 L 2,-2 L 2,2 L -2,2 Z',
							strokeColor: '#68747A',
							fillColor: '#68747A',
							fillOpacity: 1,
							strokeOpacity: 1,
							strokeWeight: 1
						},
						offset: '0%',
						repeat: '12px'
					}, {
						icon: {
							path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
							scale: 3.5,
							strokeColor: '#68747A',
							strokeWeight: 1,
							fillColor: '#68747A',
							fillOpacity: 1
						},
						offset: '100%'
					}]
				});
				this.locationHistoryPolylines.push(blackArrow);
			}
		}

		// Connect last grey marker to selectedBinInfo marker (if exists)
		if (greyMarkersItems.length > 0) {
			const lastGreyItem = greyMarkersItems[greyMarkersItems.length - 1];
			if (lastGreyItem.bin_lat && lastGreyItem.bin_lon && selectedBinLat!=null && selectedBinLng!=null) {
				const blackArrow = new google.maps.Polyline({
					path: [
						{ lat: Number(lastGreyItem.bin_lat), lng: Number(lastGreyItem.bin_lon) },
						{ lat: selectedBinLat, lng: selectedBinLng }
					],
					geodesic: true,
					strokeColor: '#68747A',
					strokeOpacity: 0,
					strokeWeight: 1,
					map: this.map,
					icons: [{
						icon: {
							path: 'M -2,-2 L 2,-2 L 2,2 L -2,2 Z',
							strokeColor: '#68747A',
							fillColor: '#68747A',
							fillOpacity: 1,
							strokeOpacity: 1,
							strokeWeight: 1
						},
						offset: '0%',
						repeat: '12px'
					}, {
						icon: {
							path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
							scale: 3.5,
							strokeColor: '#68747A',
							strokeWeight: 1,
							fillColor: '#68747A',
							fillOpacity: 1
						},
						offset: '100%'
					}]
				});
				this.locationHistoryPolylines.push(blackArrow);
			}
		}
	}

  requestGpsSync(){
	if(this.gpsRequested){
		return;
	}
	this.gpsRequested = true;
	const dataToSend = {
		"is_gps_enabled" : "1",
		"bin_id_list" : [[Number(this.selectedBinInfo.bin_id),Number(this.selectedBinInfo.cluster_id)]]			
	};
	this.apiService.setBinThresholdsAndRestrictions(dataToSend).pipe(takeUntil(this.destroy$)).subscribe((v:any) => {
		this.apiService.getBinInfo(this.selectedBinInfo.bin_id);
		this.apiService.getfilteredBinsInfo(JSON.parse(sessionStorage.getItem("chosenSites")));
		this.apiStore.update({ allFilteredBasicBins: [] });                    
		this.apiStore.update({ filteredBasicBins: [] }); 
		this.apiService.getfilteredBasicBinsInfo(JSON.parse(sessionStorage.getItem("chosenSites")));
	});
  }

  BinToIcon(status: number) {
		let icon = '';
		icon = `../../assets/images/bins/all/current_status.svg`;
		return icon;
	}

  addYourLocationButton(map) {
		let controlDiv = document.createElement('div');
		let firstChild = document.createElement('button');
		firstChild.type = 'button';
		firstChild.style.backgroundColor = '#fff';
		firstChild.style.border = 'none';
		firstChild.style.outline = 'none';
		firstChild.style.width = '40px';
		firstChild.style.height = '40px';
		firstChild.style.borderRadius = '2px';
		firstChild.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
		firstChild.style.cursor = 'pointer';
		firstChild.style.marginRight = '10px';
		firstChild.style.padding = '0px';
		firstChild.title = 'Your Location';
		controlDiv.appendChild(firstChild);
		
		let secondChild = document.createElement('div');
		secondChild.style.margin = 'calc(50% - 18px / 2)';
		secondChild.style.width = '18px';
		secondChild.style.height = '18px';
		secondChild.style.backgroundImage = 'url(https://maps.gstatic.com/tactile/mylocation/mylocation-sprite-1x.png)';
		secondChild.style.backgroundSize = '180px 18px';
		secondChild.style.backgroundPosition = '0px 0px';
		secondChild.style.backgroundRepeat = 'no-repeat';
		secondChild.id = 'center-bininfo';
		firstChild.appendChild(secondChild);
		
		google.maps.event.addListener(map, 'dragend', function() {
			document.getElementById('center-bininfo').style.backgroundPosition = '0px 0px';
		});

		firstChild.addEventListener('click', function() {
			var imgX = '0';
			var animationInterval = setInterval(function(){
				if(imgX == '-18') imgX = '0';
				else imgX = '-18';
				document.getElementById('center-bininfo').style.backgroundPosition = imgX+'px 0px';
			}, 500);
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition( pos => {        
				map.setCenter({lat: pos.coords.latitude, lng: pos.coords.longitude});
				map.setZoom(15);
				clearInterval(animationInterval);
				document.getElementById('center-bininfo').style.backgroundPosition = '-144px 0px';
				}, error => {
				},{maximumAge:10000, timeout:5000, enableHighAccuracy: true});
			}else{
				clearInterval(animationInterval);
				document.getElementById('center-bininfo').style.backgroundPosition = '0px 0px';
			}
		});
		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(controlDiv);
	}

  calcDiff = (date: string) => {
		if (this.translationsObj != undefined) {
			let diff_in_string = '';
			const original_date = moment(date);
			const date_time_now = moment();
			const diff_in_object: any = this.utilService.preciseDiffBetweenDates(original_date, date_time_now, true);
			if (diff_in_object.months > 0) {
				diff_in_string = diff_in_string + diff_in_object.months + ' ';
				if (diff_in_object.months === 1) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.MONTH + ' ';
				} else {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.MONTHS + ' ';
				}
			}
			if (diff_in_object.days > 0) {
				diff_in_string = diff_in_string + diff_in_object.days + ' ';
				if (diff_in_object.days === 1) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.DAY + ' ';
				} else {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.DAYS + ' ';
				}
			}
			if (diff_in_object.hours > 0) {
				if (diff_in_object.days > 0) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.AND + ' ' + diff_in_object.hours + ' ';
				} else {
					diff_in_string += diff_in_object.hours + ' ';
				}
				if (diff_in_object.hours === 1) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.HOUR + ' ';
				} else {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.HOURS + ' ';
				}
			}
			
			if (diff_in_object.minutes > 0) {
				if (diff_in_object.hours > 0) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.AND + ' ' + diff_in_object.minutes + ' ';
				} else {
					diff_in_string += diff_in_object.minutes + ' ';
				}
				if (diff_in_object.minutes === 1) {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.MINUTE + ' ';
				} else {
					diff_in_string += this.translationsObj.COLLECTION_REPORT.MINUTES + ' ';
				}
			}
			else {
				diff_in_string += this.translationsObj.COLLECTION_REPORT.MINUTE + ' ';
			}

			if (this.currentLang !== 'iw') {
				diff_in_string += this.translationsObj.COLLECTION_REPORT.AGO;
			}
			else {
				diff_in_string = this.translationsObj.COLLECTION_REPORT.AGO + ' ' + diff_in_string;
			}
			return diff_in_string;
		}	
	}

	getAddressFromCoordinates(latitude: number, longitude: number) {
		const geocoder = new google.maps.Geocoder();
		const latlng = {
			lat: latitude,
			lng: longitude
		};

		// Set language based on currentLang
		const language = this.currentLang === 'iw' ? 'he' : 'en';

		geocoder.geocode(
			{ 
				location: latlng,
				language: language
			},
			(
				results: google.maps.GeocoderResult[],
				status: google.maps.GeocoderStatus
			) => {
				if (status === 'OK') {
					if (results[0]) {
						this.expectedApprovalAddress = results[0].formatted_address;
					} else {
						this.expectedApprovalAddress = '';
					}
				} else {
					this.expectedApprovalAddress = '';
				}
			}
		);
	}
}
